import DatePicker from '@/shared/components/DatePicker';
import TimePicker from '@/shared/components/TimePicker';
import {setRequiredLabel} from '@/utils/helpers';
import {Field, useFormikContext} from 'formik';
import {TableAndTicketsFormStyle} from './TableAndTicketsForm.style';
import {Box, IconButton, Tooltip, Typography} from '@mui/material';
import {
  ADD_TICKET_TIER,
  BODY1,
  DELETE_UPPER,
  FIELD,
  FIELD_LABEL,
  NUMBER,
  PLACEHOLDER,
  SUBTITLE1,
  TABLE_NAME_TEXT,
  TEXT,
  TOP,
} from '@/utils/constants';
import Button from '@/shared/components/Button';
import Input from '@/shared/components/Inputs/Input';
import {ReactComponent as QuestionIcon} from '@/assets/icons/modal-question.svg';
import {ReactComponent as PlusIcon} from '@/assets/icons/plus.svg';
import {ReactComponent as ArrowDownIcon} from '@icons/select-arrow-down.svg';
import {initialTableInfos} from '@/components/Organization/Dashboard/CreateNewEvent/TableAndTickets/TicketsTables/helpers';
import {CreateEventFormValues} from '@/components/Organization/Dashboard/CreateNewEvent/formConfiguration';
import Switch from '@/shared/components/Switches/Switch';
import {TreeItem, TreeView} from '@mui/x-tree-view';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import CurrencyField from '@/shared/components/Inputs/CurrencyInput';
import {useAppSelector} from '@/store/hooks';
import {getTeamSelector} from '@/store/organization';
import {ITeamRole} from '@/utils/enums';
import {ChangeEvent, useState} from 'react';

const TablesAndTicketsForm = () => {
  const {setFieldValue, values} = useFormikContext<CreateEventFormValues>();
  const team = useAppSelector(getTeamSelector);
  // console.log('data', values.tableInfos);

  const [copiedId, setCopiedId] = useState<number | null>(null);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const addTableClickHandler = () => {
    const nextId = values.tableInfos.length ? Math.max(...values.tableInfos.map((t) => t.id)) + 1 : 1;

    setFieldValue('tableInfos', [...values.tableInfos, {...initialTableInfos, id: nextId}]);
  };

  const removeTableClickHandler = async (id: number) => {
    const tableToRemove = values.tableInfos.find((table) => table.id === id);
    if (!tableToRemove) return;

    const newTableInfos = values.tableInfos.filter((table) => table.id !== id);
    setFieldValue('tableInfos', newTableInfos);
  };

  return (
    <>
      {/* SALES DATES */}
      <Box sx={TableAndTicketsFormStyle.wrapper}>
        <Box sx={{width: '100%'}}>
          <Typography
            variant={SUBTITLE1}
            sx={{...TableAndTicketsFormStyle.title, ...(isStandartMember && {color: 'text.disabled'})}}
          >
            Start Sales On:
          </Typography>

          <Box sx={TableAndTicketsFormStyle.date}>
            <Field
              id={FIELD.ticketsSalesStartDate}
              name={FIELD.ticketsSalesStartDate}
              label={FIELD_LABEL.startDate}
              component={DatePicker}
              sx={TableAndTicketsFormStyle.dateMonthYear}
              disabled={isStandartMember}
            />

            <Field
              id={FIELD.ticketsSalesStartTime}
              name={FIELD.ticketsSalesStartTime}
              label={FIELD_LABEL.startTime}
              component={TimePicker}
              parentClassName={TableAndTicketsFormStyle.time}
              disabled={isStandartMember}
            />
          </Box>
        </Box>

        <Box sx={{width: '100%'}}>
          <Typography
            variant={SUBTITLE1}
            sx={{...TableAndTicketsFormStyle.title, ...(isStandartMember && {color: 'text.disabled'})}}
          >
            Stop Sales On:
          </Typography>

          <Box sx={TableAndTicketsFormStyle.date}>
            <Field
              id={FIELD.ticketsSalesEndDate}
              name={FIELD.ticketsSalesEndDate}
              label={FIELD_LABEL.endDate}
              component={DatePicker}
              sx={TableAndTicketsFormStyle.dateMonthYear}
              disabled={isStandartMember}
            />

            <Field
              id={FIELD.ticketsSalesEndTime}
              name={FIELD.ticketsSalesEndTime}
              label={FIELD_LABEL.endTime}
              component={TimePicker}
              parentClassName={TableAndTicketsFormStyle.time}
              disabled={isStandartMember}
            />
          </Box>
        </Box>
      </Box>

      {/* TABLES & TICKETS */}
      <Box>
        <Typography
          variant={SUBTITLE1}
          sx={{...TableAndTicketsFormStyle.titleTable, ...(isStandartMember && {color: 'text.disabled'})}}
        >
          Tables & Tickets
        </Typography>
        {values.tableInfos.map((table, index) => {
          const basePath = `tableInfos[${index}]`;

          return (
            <Box key={table.id} sx={{marginBottom: '20px'}}>
              <Box sx={TableAndTicketsFormStyle.content}>
                <Field
                  name={`${basePath}.tier`}
                  label={
                    index === 0 ? (
                      <Box sx={{display: 'flex', alignItems: 'center', ...(isStandartMember && {opacity: 0.5})}}>
                        <span>{setRequiredLabel(FIELD_LABEL.ticketsTier)}</span>
                        <Tooltip title={TABLE_NAME_TEXT} placement={TOP}>
                          <Box
                            sx={{
                              ...TableAndTicketsFormStyle.coverFeeTooltip,
                              ...(isStandartMember && {pointerEvents: 'none'}),
                            }}
                          >
                            <QuestionIcon />
                          </Box>
                        </Tooltip>
                      </Box>
                    ) : (
                      setRequiredLabel(FIELD_LABEL.ticketsTier)
                    )
                  }
                  placeholder={PLACEHOLDER.ticketsTier}
                  component={Input}
                  isArrayData
                  sx={TableAndTicketsFormStyle.ticketsTier}
                  disabled={isStandartMember}
                />
                <Field
                  name={`${basePath}.amount`}
                  label={
                    index === 0 ? (
                      <Box sx={{display: 'flex', alignItems: 'center', ...(isStandartMember && {opacity: 0.5})}}>
                        <span>{setRequiredLabel(FIELD_LABEL.numberOfTables)}</span>
                        <Tooltip title="How many of these tickets or tables are you selling?" placement={TOP}>
                          <Box
                            sx={{
                              ...TableAndTicketsFormStyle.coverFeeTooltip,
                              ...(isStandartMember && {pointerEvents: 'none'}),
                            }}
                          >
                            <QuestionIcon />
                          </Box>
                        </Tooltip>
                      </Box>
                    ) : (
                      setRequiredLabel(FIELD_LABEL.numberOfTables)
                    )
                  }
                  placeholder="0"
                  component={Input}
                  isArrayData
                  inputProps={{
                    inputMode: 'numeric',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    },
                  }}
                  disabled={isStandartMember}
                />

                <Field
                  name={`${basePath}.numberOfSeats`}
                  label={
                    index === 0 ? (
                      <Box sx={{display: 'flex', alignItems: 'center', ...(isStandartMember && {opacity: 0.5})}}>
                        <span>{setRequiredLabel(FIELD_LABEL.numberOfSeats)}</span>
                        <Tooltip title="How many seats come with each table or ticket?" placement={TOP}>
                          <Box
                            sx={{
                              ...TableAndTicketsFormStyle.coverFeeTooltip,
                              ...(isStandartMember && {pointerEvents: 'none'}),
                            }}
                          >
                            <QuestionIcon />
                          </Box>
                        </Tooltip>
                      </Box>
                    ) : (
                      setRequiredLabel(FIELD_LABEL.numberOfSeats)
                    )
                  }
                  placeholder="0"
                  component={Input}
                  isArrayData
                  inputProps={{
                    inputMode: 'numeric',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    },
                  }}
                  disabled={isStandartMember}
                />

                <CurrencyField
                  name={`${basePath}.price`}
                  label={setRequiredLabel(FIELD_LABEL.price)}
                  placeholder="0"
                  isArrayData
                  disabled={isStandartMember}
                />

                <CurrencyField
                  name={`${basePath}.fairMarketValue`}
                  label={FIELD_LABEL.fairMarketValue}
                  placeholder="0"
                  isArrayData
                  disabled={isStandartMember}
                />

                <ActionsMenu
                  parentClassName={{marginLeft: '-10px', ...(isStandartMember && {pointerEvents: 'none'})}}
                  disableRipple
                  content={
                    <Box>
                      <Typography
                        onClick={() => removeTableClickHandler(table.id)}
                        variant={BODY1}
                        sx={TableAndTicketsFormStyle.menuItem}
                      >
                        {DELETE_UPPER}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <TreeView
                sx={{...TableAndTicketsFormStyle.tree, ...(isStandartMember && {pointerEvents: 'none', opacity: 0.5})}}
                defaultCollapseIcon={<ArrowDownIcon style={TableAndTicketsFormStyle.arrowTop} />}
                defaultExpandIcon={<ArrowDownIcon />}
              >
                <TreeItem
                  nodeId="1"
                  label={
                    <Box>
                      <Box sx={{fontSize: '14px', marginLeft: '4px'}}>Advanced Settings</Box>
                    </Box>
                  }
                >
                  <TreeItem
                    nodeId="2"
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: '12px',
                          padding: '8px 0',
                        }}
                      >
                        <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                          <Field
                            name={`${basePath}.displayRemainingTickets`}
                            component={Switch}
                            sx={{marginLeft: '12px'}}
                            isArrayData
                            checked={table.displayRemainingTickets}
                          />
                          <Typography sx={{fontSize: '14px'}}>Display remaining quantity avaliable</Typography>
                        </Box>

                        <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                          <Typography sx={{marginLeft: '12px', fontSize: '14px'}}>
                            Only display when quantity is fewer than
                          </Typography>
                          <Box>
                            <Field
                              name={`${basePath}.remainingTicketsThresholdValue`}
                              component={Input}
                              type={NUMBER}
                              placeholder="0"
                              value={table.remainingTicketsThresholdValue || ''}
                              sx={{width: '100px'}}
                              isArrayData
                              disabled={!table.displayRemainingTickets}
                            />
                          </Box>
                        </Box>
                      </Box>
                    }
                  />
                  <TreeItem
                    nodeId="3"
                    label={
                      <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px', width: '100%'}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                          <Field
                            name={`${basePath}.isTicketsVisibleOnAuction`}
                            component={Switch}
                            sx={{marginLeft: '12px'}}
                            isArrayData
                            checked={!table.isTicketsVisibleOnAuction}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              setFieldValue(`${basePath}.isTicketsVisibleOnAuction`, !e.target.checked);
                            }}
                          />

                          <Typography sx={{fontSize: '14px'}}>
                            Private Package: Private Packages are only accessed by a special URL and are not publicly
                            listed.
                          </Typography>
                        </Box>

                        {table.isTicketsVisibleOnAuction === false && (
                          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '64px'}}>
                            {table?.privatePurchaseUrl ? (
                              <>
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    color: 'text.secondary',
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {table.privatePurchaseUrl}
                                </Typography>

                                <Tooltip title={copiedId === table.id ? 'Copied!' : 'Copy link'}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(table.privatePurchaseUrl || '');
                                      setCopiedId(table.id);
                                      setTimeout(() => setCopiedId(null), 1500);
                                    }}
                                  >
                                    <Typography sx={{fontSize: '14px'}}>{copiedId === table.id ? '✓' : '⧉'}</Typography>
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  color: 'text.secondary',
                                  fontStyle: 'italic',
                                }}
                              >
                                The link will be available after you save this event.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </TreeItem>
              </TreeView>
            </Box>
          );
        })}

        <Button
          title={ADD_TICKET_TIER}
          variant={TEXT}
          startIcon={<PlusIcon />}
          disableRipple
          sx={TableAndTicketsFormStyle.button}
          onClick={addTableClickHandler}
          disabled={isStandartMember}
        />
      </Box>
    </>
  );
};

export default TablesAndTicketsForm;
